import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Checkbox, CheckboxProps, Form, Button, Header, Grid, Loader, Dimmer } from 'semantic-ui-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import _ from 'lodash';
import axiosInstance from '../../helpers/axiosInstance';
import { NeighborhoodModel } from '../../types/NeighborhoodDTO';
import UserInfo from '../Common/SharedComponents/UserInfo';
import AppContext, { token_config } from '../../AppContext';
import SwitchTheme from '../Common/SharedComponents/SwitchTheme';

type FormErrorFields = {
  displayName: string;
};

export const NeighborhoodForm: React.FC = () => {
  const navigate = useNavigate();
  const { userInfo } = useContext(AppContext);

  useEffect(() => {
    if (userInfo !== undefined) {
      const privileges: string[] = userInfo ? userInfo.Privileges.split(',') : [];
      if (!privileges.includes(token_config.MANAGE_NEIGHBORHOODS)) {
        navigate('/');
      }
    }
  }, [userInfo, navigate]);

  const { id } = useParams();

  let neighborhoodId = id ? parseInt(id) : 0;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<NeighborhoodModel>({} as NeighborhoodModel);
  const [formErrors, setFormErrors] = useState<FormErrorFields>({
    displayName: ''
  });

  useEffect(() => {
    if (neighborhoodId > 0) {
      document.title = 'Edit Neighborhood - Adjure';
    } else {
      document.title = 'Add Neighborhood - Adjure';
    }
    fetchData();
  }, [neighborhoodId]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      let url = '/AdminSetting/GetNeighborhoodView';
      if (neighborhoodId > 0) {
        url = `/AdminSetting/GetNeighborhood/${neighborhoodId}`;
      }
      await axiosInstance.get(url).then(({ data, status }) => {
        if (status === 200) {
          if (data.wardIds === null) {
            data.wardIds = [];
          }
          if (data.neighborhoodId === null) {
            data.neighborhoodId = 0;
          }
          setFormValues(data);
        }
        setIsLoading(false);
      })
        .catch((error) => {
          setIsLoading(false);
          console.log(error);
        });
    } catch (error: any) {
      setIsLoading(false);
      console.log(error);
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleCheckboxChange = (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps, field: string) => {
    setFormValues({ ...formValues, [field]: data.checked });
  };

  const handleWardCheckboxChange = (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps, wardId: number) => {
    const tmpWardIds = _.cloneDeep(formValues.wardIds);
    if (data.checked) {
      tmpWardIds.push(wardId);
    } else {
      tmpWardIds.splice(tmpWardIds.indexOf(wardId), 1);
    }
    setFormValues({ ...formValues, wardIds: tmpWardIds });
  };


  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    validate(false, name, value);
  };

  const validate = async (validateAll: boolean, fieldName: string | null = null, value: any | null = null): Promise<FormErrorFields> => {
    return await new Promise((resolve, reject) => {
      let errors: FormErrorFields = { ...formErrors };
      try {
        if (validateAll) {
          errors = {
            displayName: !formValues?.displayName ? 'Name is required' : (!formValues.displayName.trim() ? 'Name is required' : ''),
          };
        } else {
          switch (fieldName) {
            case 'displayName':
              errors.displayName = !value ? 'Name is required' : (!value.trim() ? 'Name is required' : '');
              break;
            default:
              break;
          }
        }
        setFormErrors(errors);
        resolve(errors);
      } catch (error) {
        console.log(`Validation error`);
        reject(errors);
      }
    });
  }

  const handleSubmit = async () => {
    const errors = await validate(true);
    const hasErrors = Object.values(errors).some((error) => error);
    if (!hasErrors) {
      // handle form submission 
      let payload = { ...formValues };
      if (payload.neighborhoodId === undefined) {
        payload.neighborhoodId = 0;
      }
      payload.displayName = payload.displayName ? payload.displayName.trim() : '';
      axiosInstance.post('/AdminSetting/AddUpdateNeighborhood', payload).then(({ data, status, statusText }) => {
        if (status === 200) {
          if (neighborhoodId === 0) {
            toast.success('Neighborhood added successfully');
          } else {
            toast.success('Neighborhood updated successfully');
          }
          navigate('/neighborhoods');
        }
      });
    }
  };

  return (
    <section className="section" style={{
      minHeight: '100vh'
    }}>
      <div className="section__head">
        <div className="section__head-content">
          <Header as="h1">{neighborhoodId === 0 ? 'Add' : 'Edit'} Neighborhood</Header>
        </div>
        <div className="section__actions">
          <UserInfo />
          <SwitchTheme />
        </div>
      </div>
      <div className='section__body'>
        {
          <Dimmer.Dimmable as="div" dimmed={isLoading}>
            <Dimmer active={isLoading} inverted={true}>
              <Loader>Loading</Loader>
            </Dimmer>
            <Grid padded>
              <Grid.Row>
                <Grid.Column mobile={16} tablet={8} computer={8}>
                  <Form className='form-custom'>
                    <Form.Field>
                      <Form.Input
                        label="Name"
                        name="displayName"
                        value={formValues.displayName}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={!!formErrors?.displayName}
                        required
                        maxLength={50}
                      />
                      {formValues.displayName?.length === 50 && <div className="error-message" style={{ color: 'red' }}>Maximum character limit exceeded</div>}
                      {formErrors?.displayName && <div className="error-message" style={{ color: 'red' }}>{formErrors?.displayName}</div>}
                    </Form.Field>
                    <Form.Field>
                      <label>Ward</label>
                      {formValues.master?.ward.map(item => {
                        return (
                          <Checkbox
                            label={item.name}
                            value={'true'}
                            checked={(formValues.wardIds && formValues.wardIds.indexOf(item.id) !== -1) ? true : false}
                            onChange={(e, data) => handleWardCheckboxChange(e, data, item.id)}
                            onBlur={handleBlur}
                            style={{
                              margin: '1rem 1rem 0rem 1rem',
                              fontSize: '1.6rem'
                            }}
                          />
                        )
                      })}
                    </Form.Field>
                    <Form.Field>
                      <Checkbox
                        label='Active'
                        value={'true'}
                        checked={formValues.isActive === true}
                        onChange={(e, data) => handleCheckboxChange(e, data, 'isActive')}
                        onBlur={handleBlur}
                        style={{
                          margin: '1rem 1rem 0rem 1rem',
                          fontSize: '1.6rem'
                        }}
                      />
                    </Form.Field>
                    <Grid>
                      <Grid.Column>
                        <Button type="submit" onClick={handleSubmit} className='violet right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem' }}>
                          {neighborhoodId === 0 ? 'Save' : 'Update'}
                        </Button> {' '}
                        <Button type="button" className='second right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem', color: 'white' }} onClick={() => navigate('/neighborhoods')}>
                          Cancel
                        </Button>
                      </Grid.Column>
                    </Grid>
                  </Form>
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <br />
          </Dimmer.Dimmable>
        }
      </div>
    </section>
  );
}

export default NeighborhoodForm;